import { useEffect, useMemo, useState } from 'react';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import { apiFetch } from '@/lib/api/client';
import { primaryRole, rolesFromGroups, type Tier1Role } from '@/lib/auth/rbac';
import type { Principal, AuthTier } from '@/lib/auth/principal';
import { usePinSession } from '@/lib/auth/pin/usePinSession';

export interface UserContextValue {
  user: AccountInfo | null;
  isAuthenticated: boolean;
  tier: AuthTier | null;
  groups: string[];
  roles: Tier1Role[];
  primaryRole: Tier1Role | null;
  scopedStores: string[];
  /** Tier-2 only: true when the active PIN session must change PIN before any other call. */
  mustChangePin: boolean;
  /** True while /me is in flight on first load. */
  loading: boolean;
  principal: Principal | null;
  error: Error | null;
}

function groupsFromAccount(account: AccountInfo | null): string[] {
  if (!account?.idTokenClaims) return [];
  const claims = account.idTokenClaims as Record<string, unknown>;
  const raw = claims.groups;
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === 'string');
  return [];
}

/**
 * useUserContext — the single hook the rest of the app uses to read auth state.
 * Resolves whichever tier is active in the current tab:
 *   - Tier-1 (manager): MSAL account + groups + /me-resolved Principal.
 *   - Tier-2 (store_ops): PIN session's bundled Principal (no /me hop).
 */
export function useUserContext(): UserContextValue {
  const { accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? undefined);
  const { session: pinSession } = usePinSession();

  const [tier1Principal, setTier1Principal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Only fetch /me when an Entra session is active. PIN sessions ship the
  // Principal in the login response so we already have it.
  useEffect(() => {
    if (!isMsalAuthenticated || !account) {
      setTier1Principal(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<Principal>('/me')
      .then((p) => {
        if (!cancelled) setTier1Principal(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isMsalAuthenticated, account]);

  return useMemo<UserContextValue>(() => {
    if (pinSession) {
      const p = pinSession.principal;
      return {
        user: null,
        isAuthenticated: true,
        tier: 'store_ops',
        groups: p.groups,
        roles: [],
        primaryRole: null,
        scopedStores: p.scopedStoreIds,
        mustChangePin: pinSession.mustChangePin,
        loading: false,
        principal: p,
        error: null,
      };
    }
    if (isMsalAuthenticated) {
      const tokenGroups = groupsFromAccount(account);
      const groups = tier1Principal?.groups ?? tokenGroups;
      const roles = rolesFromGroups(groups);
      return {
        user: account ?? null,
        isAuthenticated: true,
        tier: 'manager',
        groups,
        roles,
        primaryRole: primaryRole(groups),
        scopedStores: tier1Principal?.scopedStoreIds ?? [],
        mustChangePin: false,
        loading,
        principal: tier1Principal,
        error,
      };
    }
    return {
      user: null,
      isAuthenticated: false,
      tier: null,
      groups: [],
      roles: [],
      primaryRole: null,
      scopedStores: [],
      mustChangePin: false,
      loading: false,
      principal: null,
      error,
    };
  }, [account, isMsalAuthenticated, tier1Principal, loading, error, pinSession]);
}
